import { TFunction } from "i18next";

import {
  formatPrice,
  IBarGraphContent,
  IReportApiDto,
  IReportCriteriaFilters,
  IReportCriteriaFiltersSchemaValues,
  IReportGridColumn,
  isEmptyFilterValue,
  ITableDto,
  normalizeReportGridValue,
  TimeZoneEnum,
} from "@ryde/shared";

import { REPORT_CHART_GROUPING_DIMENSION_VALUE } from "../../../reports/drawers/report-chart-drawer/parts/report-chart.constants";
import {
  aggregateReportRows,
  aggregateReportRowsByReportGrouping,
} from "../../../reports/drawers/report-chart-drawer/parts/report-chart.utils.data";
import {
  DashboardChartType,
  IDashboardCard,
  IMultiSeriesContent,
  TMetricUnit,
} from "../dynamic-dashboard.models";
import {
  defaultValueFormatter,
  EMPTY_CONTENT,
} from "./dashboard-card-data.constants";
import {
  IReducedCardData,
  TValueFormatter,
} from "./dashboard-card-data.models";

const isMultiSeriesType = (chartType: DashboardChartType): boolean =>
  chartType === DashboardChartType.Clustered ||
  chartType === DashboardChartType.Stacked;

/**
 * Wrap the applied dashboard filters (raw `{ key: value }`) into the report
 * criteria shape (`{ key: { value } }`), dropping empties. Each report applies
 * only the keys it declares; the engine ignores the rest.
 */
export const buildUserFilters = (
  applied: IReportCriteriaFiltersSchemaValues,
): IReportCriteriaFilters => {
  const filters: IReportCriteriaFilters = {};
  Object.entries(applied ?? {}).forEach(([key, value]) => {
    if (!isEmptyFilterValue(value)) {
      filters[key] = { value };
    }
  });
  return filters;
};

/**
 * The card's effective filter values: the dashboard-wide bar, with the card's
 * own filters laid over it key by key. A card's filter therefore survives
 * whatever the bar does to the same key, while the bar keeps narrowing every
 * other key. Card keys left empty are skipped so they defer to the bar rather
 * than silently cancelling it.
 *
 * Overlaying REPLACES rather than intersects, deliberately: a per-card filter is
 * how a user pins one card to one supplier while the bar shows another, which is
 * the whole point of per-card filters. Intersecting would make such a card render
 * empty and look broken. Nothing is exposed by this — the bar is a convenience,
 * not a permission boundary; the backend intersects every requested id with what
 * the user may see (`narrowRequestedIdsToPermitted`) and the reports engine
 * applies row-level permissions of its own.
 */
export const mergeCardFilters = ({
  appliedFilters,
  cardFilters,
}: {
  appliedFilters: IReportCriteriaFiltersSchemaValues;
  cardFilters: IReportCriteriaFiltersSchemaValues | undefined;
}): IReportCriteriaFiltersSchemaValues => {
  const merged: IReportCriteriaFiltersSchemaValues = { ...appliedFilters };
  Object.entries(cardFilters ?? {}).forEach(([key, value]) => {
    if (!isEmptyFilterValue(value)) {
      merged[key] = value;
    }
  });
  return merged;
};

export const buildCriteria = (
  card: IDashboardCard,
  filters: IReportCriteriaFilters,
) => ({
  id: card.reportId,
  filters,
  flexGrouping: {
    periodGrouping: card.periodGrouping,
    groupingParams: card.groupingParams ?? [],
  },
});

/** The metric's unit, for cards that render the raw value and append a unit themselves. */
const resolveMetricUnit = (
  metricColumn: IReportGridColumn,
): TMetricUnit | undefined => {
  if (metricColumn.isPrice) {
    return "currency";
  }

  return metricColumn.isPercentage ? "percent" : undefined;
};

export const sumMetric = (dto: IReportApiDto, metricKey: string): number =>
  (dto.rows ?? []).reduce((sum, row) => {
    const value = Number(row[metricKey]);
    return Number.isNaN(value) ? sum : sum + value;
  }, 0);

/**
 * Translate/format a dimension value the same way the reports grid does — a
 * column's `translationMap` maps enum/status values (e.g. "New" → "חדש"), and
 * dates/numbers are formatted. Falls back to the raw string when there's no
 * matching column (e.g. the composite report-grouping dimension).
 */
const normalizeLabel = (
  rawValue: unknown,
  column: IReportGridColumn | undefined,
  timezone: TimeZoneEnum,
  t: TFunction,
): string => {
  if (!column) {
    return String(rawValue ?? "");
  }
  const normalized = normalizeReportGridValue({
    value: rawValue,
    reportGridColumn: column,
    timezone,
    t,
  });
  return String(normalized ?? rawValue ?? "");
};

/** Reduce two-dimension rows into clustered/stacked series (category × series → summed metric). */
const reduceMultiSeries = (
  rows: ITableDto[],
  categoryKey: string,
  seriesKey: string,
  metricKey: string,
): IMultiSeriesContent => {
  const categories: string[] = [];
  const seriesLabels: string[] = [];
  const totals = new Map<string, Map<string, number>>();

  rows.forEach(row => {
    const category = String(row[categoryKey] ?? "");
    const series = String(row[seriesKey] ?? "");
    if (!category || !series) {
      return;
    }
    const value = Number(row[metricKey]);
    if (Number.isNaN(value)) {
      return;
    }
    if (!categories.includes(category)) {
      categories.push(category);
    }
    if (!seriesLabels.includes(series)) {
      seriesLabels.push(series);
    }
    let byCategory = totals.get(category);
    if (!byCategory) {
      byCategory = new Map();
      totals.set(category, byCategory);
    }
    byCategory.set(series, (byCategory.get(series) ?? 0) + value);
  });

  return {
    categories,
    series: seriesLabels.map(label => ({
      label,
      values: categories.map(category => totals.get(category)?.get(label) ?? 0),
    })),
  };
};

export const reduceCardData = (
  card: IDashboardCard,
  dto: IReportApiDto,
  t: TFunction,
  timezone: TimeZoneEnum,
): IReducedCardData => {
  const columns = dto.layout?.columns ?? [];
  const rows = dto.rows ?? [];
  const metricKey = card.metricKey ?? "";
  const dimensionKey = card.dimensionKey ?? "";
  const dimensionColumn = columns.find(
    col => col.uiTableColumn === dimensionKey,
  );

  const metricColumn = columns.find(
    col =>
      col.uiTableColumn === metricKey || col.columnTemplateKey === metricKey,
  );
  if (!metricColumn) {
    return {
      content: EMPTY_CONTENT,
      metricDenied: true,
      isEmpty: true,
      valueFormatter: defaultValueFormatter,
    };
  }

  // Format the metric like the reports grid: currency for price columns, "%"
  // for percentage columns, otherwise a localized number.
  const valueFormatter: TValueFormatter = value => {
    if (metricColumn.isPrice) {
      return formatPrice({ value, isCurrency: true });
    }
    const formatted = String(
      normalizeReportGridValue({
        value,
        reportGridColumn: metricColumn,
        timezone,
        t,
      }) ?? value,
    );
    return metricColumn.isPercentage ? `${formatted}%` : formatted;
  };

  let content: IBarGraphContent;
  if (dimensionKey === REPORT_CHART_GROUPING_DIMENSION_VALUE) {
    const params = card.groupingParams ?? [];
    const groupingColumns = columns.filter(
      col =>
        params.includes(col.columnTemplateKey) ||
        params.includes(col.uiTableColumn),
    );
    content = aggregateReportRowsByReportGrouping({
      rows,
      groupingColumns,
      metricColumnKey: metricKey,
    });
  } else {
    content = aggregateReportRows({
      rows,
      dimensionColumnKey: dimensionKey,
      metricColumnKey: metricKey,
    });
  }

  // Translate/format the dimension values (status → Hebrew, dates, …) like the grid.
  content = {
    entities: content.entities.map(entity => ({
      subLabel: normalizeLabel(entity.subLabel, dimensionColumn, timezone, t),
      value: entity.value,
    })),
  };

  if (card.chartType === DashboardChartType.Stat) {
    return {
      content,
      statValue: sumMetric(dto, metricKey),
      // The number card's template formats the value itself and appends a unit,
      // so it needs the metric's unit rather than a formatter — otherwise a cost
      // metric loses its currency sign and a percentage loses its "%".
      statUnit: resolveMetricUnit(metricColumn),
      metricDenied: false,
      isEmpty: rows.length === 0,
      valueFormatter,
    };
  }

  if (isMultiSeriesType(card.chartType)) {
    let multiSeries: IMultiSeriesContent;
    if (card.seriesKey) {
      const raw = reduceMultiSeries(
        rows,
        dimensionKey,
        card.seriesKey,
        metricKey,
      );
      const seriesColumn = columns.find(
        col => col.uiTableColumn === card.seriesKey,
      );
      multiSeries = {
        categories: raw.categories.map(category =>
          normalizeLabel(category, dimensionColumn, timezone, t),
        ),
        series: raw.series.map(entry => ({
          label: normalizeLabel(entry.label, seriesColumn, timezone, t),
          values: entry.values,
        })),
      };
    } else {
      multiSeries = {
        categories: content.entities.map(entity => entity.subLabel),
        series: [
          {
            label: card.metricLabel ?? card.title,
            values: content.entities.map(entity => entity.value),
          },
        ],
      };
    }
    return {
      content,
      multiSeries,
      metricDenied: false,
      isEmpty: multiSeries.categories.length === 0,
      valueFormatter,
    };
  }

  return {
    content,
    metricDenied: false,
    isEmpty: content.entities.length === 0,
    valueFormatter,
  };
};
